import { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import Board from "../components/Board";
import BoardFullscreenShell from "../components/BoardFullscreenShell";
import GameEndModal from "../components/GameEndModal";
import { ScorePanel } from "../components/ScorePanel";
import {
  getInitialBoardForPlay,
  getLegalMoves,
  applyMove,
  checkWinner,
  playerHasCapture,
  getSimpleMovesForPiece,
  buildSlidePath,
} from "../game/engine";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RotateCcw, Flag, Wind } from "lucide-react";

export default function GameLocal() {
  const { user } = useAuth();
  const [board, setBoard] = useState(() => getInitialBoardForPlay());
  const [turn, setTurn] = useState("A");
  const [selected, setSelected] = useState(null);
  const [captures, setCaptures] = useState({ A: 0, B: 0 });
  const [moves, setMoves] = useState(0);
  const [winner, setWinner] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [resultSaved, setResultSaved] = useState(false);
  const [animatingMove, setAnimatingMove] = useState(null);

  // Tapea state: if the previous player missed a forced capture, the OPPONENT can claim
  // the offending piece by clicking "Souffler" within a short window.
  // missedCapture = { byColor: 'A'|'B' (the player who missed), pieces: [[x,y],...] candidate pieces that had to capture }
  const [missedCapture, setMissedCapture] = useState(null);

  const allLegal = useMemo(() => (winner ? [] : getLegalMoves(board, turn)), [board, turn, winner]);

  const mustCapturePieces = useMemo(() => {
    if (winner) return new Set();
    if (!playerHasCapture(board, turn)) return new Set();
    const out = new Set();
    for (const m of allLegal) {
      if (m.captures.length) out.add(`${m.from[0]},${m.from[1]}`);
    }
    return out;
  }, [board, turn, allLegal, winner]);

  // Sans aides visuelles — règle Tapea si capture oubliée.
  const movesForPlay = useMemo(() => {
    if (winner) return [];
    if (!playerHasCapture(board, turn)) return allLegal;
    const out = [...allLegal];
    for (let y = 0; y < 5; y++) {
      for (let x = 0; x < 5; x++) {
        const p = board[y][x];
        if (!p || p.owner !== turn) continue;
        for (const [nx, ny] of getSimpleMovesForPiece(board, x, y)) {
          out.push({
            from: [x, y],
            to: [nx, ny],
            captures: [],
            path: p.king ? buildSlidePath([x, y], [nx, ny]) : [[x, y], [nx, ny]],
          });
        }
      }
    }
    return out;
  }, [allLegal, board, turn, winner]);

  const legalForSelected = useMemo(() => {
    if (!selected) return [];
    return movesForPlay.filter((m) => m.from[0] === selected[0] && m.from[1] === selected[1]);
  }, [movesForPlay, selected]);

  const saveResult = useCallback(async (winnerColor) => {
    if (!user || resultSaved) return;
    setResultSaved(true);
    try {
      await api.post("/games/result", {
        mode: "local", result: "draw", opponent: "Local",
        moves, captures: winnerColor === "A" ? captures.A : captures.B,
      });
    } catch (_) { /* ignore */ }
  }, [user, resultSaved, moves, captures]);

  const onClickIntersection = (x, y) => {
    if (winner || animatingMove) return;
    const piece = board[y][x];
    // Selecting own piece
    if (piece && piece.owner === turn) {
      setSelected([x, y]);
      return;
    }
    // Attempt move
    if (selected) {
      const m = legalForSelected.find((mv) => mv.to[0] === x && mv.to[1] === y);
      if (!m) return;
      const movingPiece = board[selected[1]][selected[0]];
      // Tapea check: did this player miss a forced capture?
      const hadForcedCapture = mustCapturePieces.size > 0;
      const wasCaptureMove = m.captures.length > 0;
      const missed = hadForcedCapture && !wasCaptureMove;
      const offenders = missed ? Array.from(mustCapturePieces).map(s => s.split(",").map(Number)) : null;

      const commit = () => {
        const newBoard = applyMove(board, m);
        const newCaptures = { ...captures };
        newCaptures[turn] += m.captures.length;
        const next = turn === "A" ? "B" : "A";
        const w = checkWinner(newBoard, next);
        setBoard(newBoard);
        setCaptures(newCaptures);
        setMoves((n) => n + 1);
        setLastMove({ from: m.from, to: m.to });
        setSelected(null);
        setAnimatingMove(null);
        // Set Tapea window for the opponent if missed capture
        if (missed && !w) {
          setMissedCapture({ byColor: turn, offenders });
        } else {
          setMissedCapture(null);
        }
        if (w) {
          setWinner(w);
          saveResult(w);
        } else {
          setTurn(next);
        }
      };
      if (m.captures.length > 0) {
        setAnimatingMove({
          path: m.path, owner: movingPiece.owner, king: movingPiece.king,
          duration: 500 + m.captures.length * 250, capturedCells: m.captures, onDone: commit,
        });
      } else {
        commit();
      }
    }
  };

  // Player whose turn it is now can "Souffler" (claim) a piece of the previous player who missed
  const souffler = () => {
    if (!missedCapture) return;
    const { byColor, offenders } = missedCapture;
    if (!offenders || offenders.length === 0) return;
    // Pick the first offending piece (or could be any). Show a toast naming it.
    const [ox, oy] = offenders[0];
    const newBoard = board.map(row => row.map(c => (c ? { ...c } : null)));
    if (newBoard[oy][ox] && newBoard[oy][ox].owner === byColor) {
      newBoard[oy][ox] = null;
      setBoard(newBoard);
      const otherColor = byColor === "A" ? "B" : "A";
      setCaptures(c => ({ ...c, [otherColor]: c[otherColor] + 1 }));
      toast.success(`Tapea ! Pion (${ox},${oy}) soufflé.`);
      // Check winner after souffler
      const w = checkWinner(newBoard, turn);
      if (w) {
        setWinner(w);
        saveResult(w);
      }
    }
    setMissedCapture(null);
  };

  const reset = () => {
    setBoard(getInitialBoardForPlay());
    setTurn("A");
    setSelected(null);
    setCaptures({ A: 0, B: 0 });
    setMoves(0);
    setWinner(null);
    setLastMove(null);
    setResultSaved(false);
    setMissedCapture(null);
  };

  const resign = () => {
    if (winner) return;
    setWinner(turn === "A" ? "B" : "A");
  };

  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 fade-up">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-xs tracking-[0.3em] text-[color:var(--primary)]">MODE LOCAL</p>
            <h1 className="text-2xl font-bold tracking-tight">Partie à deux</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={reset} className="px-3 py-2 rounded-md border border-[color:var(--border)] text-xs flex items-center gap-2 hover:bg-[color:var(--surface)]" data-testid="local-reset">
              <RotateCcw size={14} /> Recommencer
            </button>
            <button onClick={resign} className="px-3 py-2 rounded-md border border-[color:var(--border)] text-xs flex items-center gap-2 hover:bg-[color:var(--surface)]" data-testid="local-resign">
              <Flag size={14} /> Abandonner
            </button>
          </div>
        </div>

        <BoardFullscreenShell>
          <ScorePanel turn={turn} winner={winner} captures={captures} />

          {missedCapture && !winner && (
            <div className="glass rounded-lg p-4 mb-3 flex items-center justify-between gap-3 border border-orange-500/50 bg-orange-500/5" data-testid="tapea-banner">
              <div className="text-sm">
                <span className="text-orange-400 font-semibold">Tapea !</span>{" "}
                <span className="text-[color:var(--text-secondary)]">
                  {missedCapture.byColor === "A" ? "Les blancs" : "Les noirs"} ont oublié une capture obligatoire.
                  Vous pouvez souffler leur pion en ({missedCapture.offenders[0][0]},{missedCapture.offenders[0][1]}).
                </span>
              </div>
              <button
                onClick={souffler}
                className="btn-gold px-4 py-2 rounded-md text-xs flex items-center gap-2 whitespace-nowrap"
                data-testid="tapea-souffler"
              >
                <Wind size={14} /> Souffler
              </button>
            </div>
          )}

          <Board
            board={board}
            selectedFrom={selected}
            legalMovesForSelected={legalForSelected}
            mustCapturePieces={mustCapturePieces}
            onClickIntersection={onClickIntersection}
            lastMove={lastMove}
            animatingMove={animatingMove}
            capturedCells={animatingMove?.capturedCells || []}
            hintsOn={false}
          />
          <GameEndModal
            open={!!winner}
            mode="local"
            winnerSide={winner}
            onRestart={reset}
          />
        </BoardFullscreenShell>

        <p className="mt-4 text-center text-xs text-[color:var(--text-secondary)]">
          Mode strict — règle du <strong>Tapea</strong> active : oubliez une prise et votre pion peut être soufflé.
        </p>
      </main>
    </div>
  );
}
